/**
 * Canonical ALV navigation structure — Single Source of Truth.
 *
 * Used by:
 *   - src/components/Navigation.vue  (sidebar menu)
 *   - src/utils/navigationOrder.js   (Dashboard Quicklinks sorting)
 *
 * The ORDER of entries here drives the order users see in the sidebar AND
 * the order templates/guides appear in the Dashboard Quicklinks dropdowns.
 *
 * Each menu entry that maps to a real ALV domain optionally carries TWO
 * extra identifiers used for cross-system sorting:
 *
 *   - `mappingsDomain`   — value from DB column `alv_mappings.mappings_domain`
 *                          (underscores, e.g. `label_sales_data`)
 *   - `storyblokDomain`  — value from Storyblok datasource `alv-domain`
 *                          (hyphens, e.g. `label-sales-data`, AZ uses `a-z-*`)
 *
 * These are kept explicit (not auto-derived from the route) because the
 * three identifier systems (route / DB / Storyblok) are NOT mutually
 * convertible by simple separator swap — AZ uses `a-z-*` in Storyblok but
 * `az_*` in DB, ERP Invoices is `erp-invoicing` in Storyblok, etc.
 *
 * @param {Function} $t - i18n translation function (vue-i18n's this.$t).
 *                        Pass `key => key` if translated titles are not needed.
 */
export function buildNavigationItems($t) {
    return [
        {
            group: 'inside',
            mixpanel: 'Dashboard',
            title: $t('lang_DASHBOARD'),
            icon: 'dashboard',
            to: '/dashboard',
            alvqa: 'navigation-dashboard',
        },
        {
            group: 'menu',
            mixpanel: 'Publishing',
            title: $t('lang_PUBLISHING'),
            icon: 'music_note',
            alvqa: 'navigation-publishing',
            items: [
                { title: $t('lang_PUBLISHING_CAPTION_WORKS'), to: '/publishing/works',      mappingsDomain: 'publishing_works',      storyblokDomain: 'publishing-works',      alvqa: 'navigation-publishing-works',      mixpanel: 'Publishing > Works' },
                { title: $t('lang_PUBLISHING_SHEETS'),        to: '/publishing/sheets',     mappingsDomain: 'publishing_sheets',     storyblokDomain: 'publishing-sheets',     alvqa: 'navigation-publishing-sheets',     mixpanel: 'Publishing > Sheets' },
                { title: $t('lang_PUBLISHING_PROJECTS'),      to: '/publishing/projects',   mappingsDomain: 'publishing_projects',   storyblokDomain: 'publishing-projects',   alvqa: 'navigation-publishing-projects',   mixpanel: 'Publishing > Projects' },
                { title: $t('lang_PUBLISHING_INCOME'),        to: '/publishing/income',     mappingsDomain: 'publishing_income',     storyblokDomain: 'publishing-income',     alvqa: 'navigation-publishing-income',     mixpanel: 'Publishing > Income' },
                { title: $t('lang_PUBLISHING_STATEMENTS'),    to: '/publishing/statements', mappingsDomain: 'publishing_statements', storyblokDomain: 'publishing-statements', alvqa: 'navigation-publishing-statements', mixpanel: 'Publishing > Statements' },
                { title: $t('lang_PUBLISHING_STATISTICS'),    to: '/publishing/statistics', mappingsDomain: 'publishing_statistics', storyblokDomain: 'publishing-statistics', alvqa: 'navigation-publishing-statistics', mixpanel: 'Publishing > Statistics' },
                { title: $t('lang_PUBLISHING_USAGE'),         to: '/publishing/usage',      mappingsDomain: 'publishing_usage_records', storyblokDomain: 'publishing-usage', alvqa: 'navigation-publishing-usage',      mixpanel: 'Publishing > Usage' },
                // { title: $t('lang_PUBLISHING_TRACKING'), alvqa: 'navigation-publishing-tracking' },
            ],
        },
        {
            group: 'menu',
            mixpanel: 'Label',
            title: $t('lang_LABEL'),
            icon: 'album',
            alvqa: 'navigation-label',
            items: [
                { title: $t('lang_LABEL_TRACKS'),     to: '/label/tracks',     mappingsDomain: 'label_tracks',     storyblokDomain: 'label-tracks',     alvqa: 'navigation-label-tracks',     mixpanel: 'Label > Tracks' },
                { title: $t('lang_LABEL_RELEASES'),   to: '/label/releases',   mappingsDomain: 'label_releases',   storyblokDomain: 'label-releases',   alvqa: 'navigation-label-releases',   mixpanel: 'Label > Releases' },
                { title: $t('lang_LABEL_PROJECTS'),   to: '/label/projects',   mappingsDomain: 'label_projects',   storyblokDomain: 'label-projects',   alvqa: 'navigation-label-projects',   mixpanel: 'Label > Projects' },
                { title: $t('lang_LABEL_SALES_DATA'), to: '/label/salesdata',  mappingsDomain: 'label_sales_data', storyblokDomain: 'label-sales-data', alvqa: 'navigation-label-sales_data', mixpanel: 'Label > Salesdata' },
                { title: $t('lang_LABEL_STATEMENTS'), to: '/label/statements', mappingsDomain: 'label_statements', storyblokDomain: 'label-statements', alvqa: 'navigation-label-statements', mixpanel: 'Label > Statements' },
                { title: $t('lang_LABEL_STATISTICS'), to: '/label/statistics', mappingsDomain: 'label_statistics', storyblokDomain: 'label-statistics', alvqa: 'navigation-label-statistics', mixpanel: 'Label > Statistics' },
            ],
        },
        {
            group: 'menu',
            mixpanel: 'ERP',
            title: $t('lang_ERP'),
            icon: 'local_shipping',
            alvqa: 'navigation-erp',
            items: [
                { title: $t('lang_ERP_PRICELISTS'),  to: '/erp/pricelists',  mappingsDomain: 'erp_pricelists',  storyblokDomain: 'erp-pricelists',  alvqa: 'navigation-erp-pricelists',  mixpanel: 'ERP > Pricelists' },
                { title: $t('lang_ERP_INVENTORIES'), to: '/erp/inventories', mappingsDomain: 'erp_inventories', storyblokDomain: 'erp-inventories', alvqa: 'navigation-erp-inventories', mixpanel: 'ERP > Inventories' },
                { title: $t('lang_ERP_SHIPPING'),    to: '/erp/shipping',    mappingsDomain: 'erp_shipping',    storyblokDomain: 'erp-shipping',    alvqa: 'navigation-erp-shipping',    mixpanel: 'ERP > Shipping' },
                { title: $t('lang_ERP_INVOICES'),    to: '/erp/invoices',    mappingsDomain: 'erp_invoices',    storyblokDomain: 'erp-invoicing',   alvqa: 'navigation-erp-invoices',    mixpanel: 'ERP > Invoices' },
            ],
        },
        {
            group: 'menu',
            mixpanel: 'A-Z',
            title: $t('lang_GENERIC_CAPTION_AZ'),
            icon: 'group',
            alvqa: 'navigation-az',
            items: [
                { title: $t('lang_AZ_GROUPS'),       to: '/az/azgroups',       mappingsDomain: 'az_groups',       storyblokDomain: 'a-z-groups',       alvqa: 'navigation-az-groups',       mixpanel: 'A-Z > A-Z Groups' },
                { title: $t('lang_AZ_PERSONS'),      to: '/az/azpersons',      mappingsDomain: 'az_persons',      storyblokDomain: 'a-z-persons',      alvqa: 'navigation-az-persons',      mixpanel: 'A-Z > A-Z Persons' },
                { title: $t('lang_AZ_DISTRIBUTORS'), to: '/az/azdistributors', mappingsDomain: 'az_distributors', storyblokDomain: 'a-z-distributors', alvqa: 'navigation-az-distributors', mixpanel: 'A-Z > A-Z Distributors' },
                { title: $t('lang_AZ_SOCIETIES'),    to: '/az/azsocieties',    mappingsDomain: 'az_societies',    storyblokDomain: 'a-z-societies',    alvqa: 'navigation-az-societies',    mixpanel: 'A-Z > A-Z Societies' },
                { title: $t('lang_AZ_LIVE'),         to: '/az/azlive',         mappingsDomain: 'az_live',         storyblokDomain: 'a-z-live',         alvqa: 'navigation-az-live',         mixpanel: 'A-Z > A-Z Live' },
            ],
        },
        {
            group: 'menu',
            mixpanel: 'Contracts',
            title: $t('lang_CONTRACTS'),
            icon: 'pie_chart',
            alvqa: 'navigation-contracts',
            items: [
                { title: $t('lang_CONTRACTS_PUBLISHING_CONTRACTS'), to: '/contracts/publishing', mappingsDomain: 'contracts_publishing', storyblokDomain: 'contracts-publishing', alvqa: 'navigation-contracts-publishing', mixpanel: 'Contracts > Publishing' },
                { title: $t('lang_CONTRACTS_LABEL_CONTRACTS'),      to: '/contracts/label',      mappingsDomain: 'contracts_label',      storyblokDomain: 'contracts-label',      alvqa: 'navigation-contracts-label',      mixpanel: 'Contracts > Label' },
            ],
        },
        {
            group: 'menu',
            mixpanel: 'Account',
            title: $t('lang_ACCOUNT'),
            icon: 'settings',
            alvqa: 'navigation-account',
            items: [
                // { title: $t('lang_ACCOUNT_USER_ROLES'), alvqa: 'navigation-account-user-roles', mixpanel: 'Account > Roles' },
                { title: $t('lang_ACCOUNT_COMPANIES'),   to: '/account/companies',   mappingsDomain: 'account_companies',   storyblokDomain: 'account-companies',   alvqa: 'navigation-account-companies',   mixpanel: 'Account > Companies' },
                { title: $t('lang_ACCOUNT_MODULES'),     to: '/account/modules',     mappingsDomain: 'account_modules',     storyblokDomain: 'account-modules',     alvqa: 'navigation-account-modules',     mixpanel: 'Account > Modules' },
                { title: $t('lang_ACCOUNT_SETTINGS'),    to: '/account/settings',    mappingsDomain: 'account_settings',    storyblokDomain: 'account-settings',    alvqa: 'navigation-account-settings',    mixpanel: 'Account > Settings' },
                { title: $t('lang_ACCOUNT_CREDENTIALS'), to: '/account/credentials', mappingsDomain: 'account_credentials', storyblokDomain: 'account-credentials', alvqa: 'navigation-account-credentials', mixpanel: 'Account > Credentials' },
                { title: $t('lang_ACCOUNT_MAPPINGS'),    to: '/account/mappings',    mappingsDomain: 'account_mappings',    storyblokDomain: 'account-mappings',    alvqa: 'navigation-account-mappings',    mixpanel: 'Account > Mappings' },
            ],
        },
        {
            group: 'menu',
            mixpanel: 'ServiceCenter',
            title: $t('lang_NAVIGATION_SERVICE_CENTER'),
            icon: 'help_outline',
            alvqa: 'navigation-service-center',
            items: [
                { title: $t('lang_SERVICE_CENTER_CAPTION_HELP'),     to: 'https://alvdigital.atlassian.net/servicedesk/customer/portals', alvqa: 'navigation-service-center-help',     mixpanel: 'ServiceCenter > Help', external: true },
                { title: $t('lang_SERVICE_CENTER_CAPTION_INSIGHTS'), to: '/servicecenter/insights',                                       alvqa: 'navigation-service-center-insights', mixpanel: 'ServiceCenter > Insights' },
            ],
        },
    ]
}
